// Trade import from screenshots and PDFs (tradeReader.js on the server): Claude reads the trades
// out of the pictures, and they're written out as CSV text so parseTradesCsv reads them like any
// other file and the usual import preview shows them before anything is saved.
import { api } from './api';
import { featureOpen } from './AppNav';
import { parseTradesCsv } from './csv';

const READABLE = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'application/pdf'];

// Members can use it once Admin → Features opens it; admins see it first.
export const canReadPictures = (user) => Boolean(user && !user.isDemo && (user.isAdmin || featureOpen(user, 'aiImport')));
export const isPicture = (file) => Boolean(file) && (READABLE.includes(file.type) || /\.(png|jpe?g|webp|gif|pdf)$/i.test(file.name || ''));

const typeOf = (file) => file.type || (/\.pdf$/i.test(file.name) ? 'application/pdf' : /\.png$/i.test(file.name) ? 'image/png' : 'image/jpeg');
const base64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
  reader.onerror = () => reject(new Error('Couldn’t open the file'));
  reader.readAsDataURL(file);
});

const cell = (v) => {
  const s = String(v === undefined || v === null ? '' : v).trim();
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
// Prices and amounts lose their $ and thousands commas; everything else is kept as read.
const number = (v) => String(v || '').replace(/[$,\s]/g, '');

// What Claude read, as CSV text: one row per trade, or one per fill (paired into trades by
// parseTradesCsv). The account column is left out when no row shows one.
export function readToCsv(read) {
  const rows = read.kind === 'fills' ? read.fills : read.trades;
  const withAccount = rows.some((r) => String(r.account || '').trim());
  const head = read.kind === 'fills'
    ? ['Contract', 'B/S', 'Filled Qty', 'Avg Fill Price', 'Fill Time', 'Commission']
    : ['Symbol', 'Side', 'Size', 'Entry Time', 'Exit Time', 'Entry Price', 'Exit Price', 'PnL', 'Fees'];
  const lines = rows.map((r) => {
    const values = read.kind === 'fills'
      ? [r.symbol, r.side, number(r.qty), number(r.price), r.time, number(r.fees)]
      : [r.symbol, r.side, number(r.size), r.entryTime, r.exitTime, number(r.entryPrice), number(r.exitPrice), number(r.pnl), number(r.fees)];
    return [...(withAccount ? [r.account] : []), ...values].map(cell).join(',');
  });
  return [[...(withAccount ? ['Account'] : []), ...head].join(','), ...lines].join('\n');
}

// Reads the pictures and returns what an import preview needs, like a CSV file would, plus
// Claude's note about anything unclear.
export async function readPictures(files) {
  const list = [...files];
  const payload = await Promise.all(list.map(async (f) => ({ type: typeOf(f), data: await base64(f) })));
  const read = await api('/import/read', { method: 'POST', body: { files: payload } });
  const fileName = list.length === 1 ? list[0].name : `${list.length} files`;
  if (read.kind === 'none') return { fileName, trades: [], errors: [], missingColumns: [], format: { name: 'trades', platform: 'Screenshot' }, readNote: read.note, fromPicture: true };
  const result = parseTradesCsv(readToCsv(read));
  return { fileName, ...result, format: { ...result.format, platform: 'Screenshot' }, readNote: read.note, fromPicture: true };
}
